// ── What a plan card advertises ─────────────────────────────────────────────
//
// ── WHAT THIS REPLACES ──────────────────────────────────────────────────────
// A hardcoded list in PlanCard whose first three rows were the three-way credit
// illusion this project has been unwinding everywhere else:
//
//     220 SEO Content Credits
//     220 AI Image Credits
//     220 Keyword List Credits
//
// Those are the same 220. `plan-credit-sync.ts` writes the one ISEO grant into
// all three legacy columns on every boot, so the card listed a single pool
// three times and a customer reading it reasonably concluded they got 660.
//
// It now says the number once and then explains the flexibility, which is what
// a weighted pool actually is.
//
// ── ONE LIST, BUILT FROM THE PLAN ───────────────────────────────────────────
// The three tiers differ in exactly three ways — the credit figure, the website
// count, and whether priority support is included — so this is one function
// over plan data rather than three lists to keep in sync. Everything else is
// identical by design, and a tier that diverges later should gain a branch
// here rather than a fourth copy of the list.
//
// Pure, so the wording is testable without mounting a card. The one import is
// planUtils, which is pure too: it answers "which tier is this", and two rows
// now differ by tier rather than by a number on the plan.
import { getPlanDisplayName } from './planUtils';

/**
 * Rows are OBJECTS, not strings, because two of them are no longer one line:
 *
 *   { label }                      an ordinary ticked row
 *   { label, detail }              a row with a muted sub-line beneath it
 *   { label, bullets: [...] }      a row with indented dashes beneath it
 */

/**
 * How many websites this plan may connect.
 *
 * Read from `website_limit`, which is not decoration: the server enforces it
 * when a site is added and when the plugin connects one, refusing with "Your
 * plan allows N websites." Printing a literal here is how a card comes to
 * contradict the error message a customer actually hits.
 *
 * NULL means unlimited — that is how the top tier is stored.
 */
function websiteRow(plan) {
  // `strong` on every branch: the website allowance is one of the things that
  // actually differ between tiers, so it carries weight in the list.
  //
  // OLD: websiteRow(plan, advertisedWebsites), where a typed-in figure could
  // override the enforced limit. Only the backend's figure is read now.
  const limit = plan?.website_limit;
  const n = Number(limit);
  if (limit === null || limit === undefined || !Number.isFinite(n) || n <= 0) {
    // "Connect unlimited websites", not "Unlimited websites" — it reads as the
    // same promise as the numbered tiers rather than a different kind of claim.
    return { label: 'Connect unlimited websites', strong: true };
  }
  return { label: `Connect ${n} website${n === 1 ? '' : 's'}`, strong: true };
}

/** The unlimited tier is the top one, which is also the one with priority support. */
const isTopTier = (plan) => plan?.website_limit === null || plan?.website_limit === undefined;

// REMOVED: paidWebsiteAllowances(plans) — "2, 3 or unlimited websites".
// It existed for one sentence in the website-limit dialog: "Paid plans connect
// 2, 3 or unlimited websites." That dialog now names the tiers a customer can
// actually move UP to ("upgrade to the Optimize or Scale plan"), which answers
// the question the reader is asking rather than reciting the whole price list —
// so nothing called this any more. See utils/websiteLimitCopy.js.


/**
 * Claims every plan card makes, paid and Basic alike, in the order they show.
 *
 * Named once so a change to a shared claim cannot land on three cards and miss
 * the fourth — the drift buildTrialFeatures was already written to avoid.
 */
const SHARED_FEATURE_ROWS = [
  'Optimize content for conversion',
  'Unique content relevant to your business',
  'Internal linking',
  'Meta titles and descriptions',
  'Schema markup',
  // The keyword-list TOOL, which every plan gets. It was Basic's alone, listed
  // there because Basic spends no credit allowance on keyword lists — but the
  // tool is not what a paid plan buys either, so the paid cards now state it
  // the same way rather than burying it as a third flexibility bullet.
  'Keyword list generation tool',
  'Publish straight inside WordPress',
];

/**
 * ── WHERE EVERY PLAN FIGURE COMES FROM ──────────────────────────────────────
 *
 * The backend, and only the backend. Nothing in this file states a credit
 * grant, a piece estimate or a website allowance for any tier:
 *
 *   credits    `content_credits` on the plan, from GET /stripe/plans. The
 *              server's plan-credit-sync writes ISEO_GRANT_<SLUG> into it at
 *              boot — the same figure it grants at renewal and quotes as
 *              `new_allotment` in a plan-change preview.
 *   estimate   GET /credits/estimate for that grant (hooks/usePieceEstimate.js),
 *              the same endpoint the WordPress plugin reads.
 *   websites   `website_limit` on the plan, which the server enforces.
 *
 * So when a grant, the cost table or a limit changes on the server, every card
 * and every surface that reads planCredits follows with no frontend change.
 * The monthly PRICE was never here either: it comes from the same payload.
 *
 * OLD: an ADVERTISED table here pinned each tier to typed-in figures — Grow
 * 200 ≈ 10 and 3 sites, Optimize 600 ≈ 30 and 5 sites, Scale 1200 ≈ 60 —
 * overriding the payload while the server still served 220 / 580 / 1,150.
 * The server has served the new grants since, so the table had become a second
 * copy of them that the next change would have left stale. REQUESTED: the
 * backend is the single source of truth, so it went. The piece estimates come
 * from GET /credits/estimate (hooks/usePieceEstimate.js); if they should read
 * differently, that is the server's cost table to change, not a number to type
 * in here.
 */

/**
 * The monthly credit figure a plan card states — the number to ask the server
 * to estimate pieces for, so the estimate describes the figure on the card.
 */
export function planCredits(plan) {
  if (!plan) return null;
  const credits = Number(plan.content_credits);
  return Number.isFinite(credits) && credits > 0 ? credits : null;
}

/**
 * Bulk creation and scheduled publishing, which Grow does not include.
 *
 * REQUESTED: they are on Scale's list and absent from Grow's. That is the row
 * pair separating the entry tier from the ones above it, and it is why these
 * two are bold — they are what paying MORE buys, in the same way the whole
 * pair is what paying at all buys against Basic, which lists neither.
 *
 * Optimize keeps them: no list has been specified for it, so it follows the
 * tier above rather than silently losing features nobody asked to remove.
 */
const hasBulkAndScheduling = (plan) => getPlanDisplayName(plan) !== 'Grow';

/**
 * @param pieces  GET /credits/estimate for planCredits(plan); null while unknown
 */
export function buildPlanFeatures(plan, { pieces = null, yearly = false } = {}) {
  if (!plan) return [];

  // `content_credits` reads as a legacy column but IS the monthly grant:
  // plan-credit-sync writes ISEO_GRANT_* into it at boot, so it follows an env
  // change. The other two columns hold the same value and are not read.
  const credits = planCredits(plan);

  const rows = [];

  if (credits) {
    rows.push({
      label: `${credits.toLocaleString()} credits per month`,
      strong: true,
      // Two fields, one sentence: the tail is separate only because it is
      // optional, not because it is quieter. PlanFeatureList renders them as a
      // single bold-italic line — "≈ 61 pieces of local SEO content with AI
      // image" is one claim, and splitting its weight split its meaning.
      //
      // Dropped entirely when the rate has not arrived — a missing sub-line is
      // invisible, "≈ NaN pieces" is not.
      ...(pieces ? {
        detail: `≈ ${pieces.toLocaleString()} pieces of local SEO content`,
        detailTail: 'with AI image',
      } : {}),
    });
  }

  rows.push({
    label: 'Flexible: use credits as you choose for',
    // Two, not three. "Keyword lists" was here as a third way to spend the
    // pool, which is true but sells it as an allowance; the tool itself is a
    // row of its own further down, on every card — see SHARED_FEATURE_ROWS.
    bullets: ['Local SEO content generation', 'AI images'],
  });

  // What "valid for two months" MEANT, said the way a customer thinks about
  // it: this month's unused credits are still there next month. The old
  // wording stated the same window as an expiry date and read as a warning.
  rows.push({ label: 'Credits roll over one month' });
  // Bold, with the credit figure and the website allowance: these two are what
  // a subscription adds that pay-as-you-go does not have at all — the Basic
  // card omits both rows entirely — so they are a reason to pay rather than
  // another shared claim.
  if (hasBulkAndScheduling(plan)) {
    rows.push({ label: 'Bulk Post Creation', strong: true });
    rows.push({ label: 'Scheduled Publishing', strong: true });
  }
  rows.push(websiteRow(plan));
  for (const label of SHARED_FEATURE_ROWS) rows.push({ label });
  // The packs are the same ones Basic buys — a subscription just gets them
  // cheaper, which is the part worth saying. "Purchase additional credit packs
  // as needed" described a mechanism available to everyone, paid or not.
  rows.push({ label: 'Get extra credit packs at a discount' });
  // ── The cancellation promise has to match the price above it ──────────────
  // "Cancel any time" is true on both intervals, but on a yearly plan it is
  // true in a way that misleads: cancelling does not end access, it stops the
  // renewal, and the year already paid for runs to its end. Someone reading
  // the bare line next to a year's price can reasonably expect to stop paying
  // mid-year and be refunded the rest.
  //
  // Keyed off `yearly`, which the card derives as `interval === 'year' && the
  // plan HAS a yearly price` — not off the toggle alone. A plan with no yearly
  // price keeps showing its monthly price while the toggle says Yearly, and
  // this line has to describe the price on the same card, not the state of a
  // control above it.
  rows.push({
    label: yearly
      ? 'Cancel any time, active until your year ends'
      : 'Cancel any time',
  });

  // Keyed off the tier rather than a slug, so renaming a plan in the database
  // cannot silently drop the row.
  if (isTopTier(plan)) rows.push({ label: 'Priority support', strong: true });

  return rows;
}

/**
 * What the FREE TRIAL includes, for the dialog behind "See what features are
 * included" on the Active Plan card.
 *
 * ── WHY IT IS NOT buildPlanFeatures ─────────────────────────────────────────
 * A trial is not a small plan, and four of the paid rows are untrue or
 * meaningless on one:
 *
 *   "credits per month"                  a trial grants once, not monthly
 *   "Credits roll over one month"        outlives the trial itself
 *   "Get extra credit packs at a         a paid-plan affordance
 *    discount"
 *   "Cancel any time"                    there is no subscription to cancel
 *
 * It lists the shared claims one by one rather than reusing
 * SHARED_FEATURE_ROWS, for one row's sake: the keyword-list generation tool.
 * Every plan card carries it; a trial is pitched on content and images alone.
 *
 * Everything the two DO share is written once, below, so a change to the
 * shared claims cannot land on the plan cards and miss this dialog.
 *
 * @param credits         the trial's granted allotment, from the server
 * @param pieces          GET /credits/estimate for `credits`; null while unknown
 * @param websiteLimit    the account's own website_limit (see websiteRow)
 */
export function buildTrialFeatures({ credits, pieces = null, websiteLimit } = {}) {
  const rows = [];
  const n = Number(credits);

  if (Number.isFinite(n) && n > 0) {
    rows.push({
      // No "per month": the trial grant is a one-off, and saying otherwise
      // promises a renewal that never comes.
      label: `${n.toLocaleString()} credit${n === 1 ? '' : 's'}`,
      strong: true,
      ...(pieces ? {
        detail: `≈ ${pieces.toLocaleString()} piece${pieces === 1 ? '' : 's'} of local SEO content`,
        detailTail: 'with AI image',
      } : {}),
    });
  }

  rows.push({
    label: 'Flexible: use credits as you choose for',
    bullets: ['Local SEO content generation', 'AI images'],
  });

  // Dropped entirely when the limit is unknown, rather than borrowing
  // websiteRow: that helper reads a missing limit as UNLIMITED, which is how
  // the top tier stores it. On a trial the same absence means "we have not
  // been told", and "Connect unlimited websites" is the worst possible guess.
  const limit = Number(websiteLimit);
  if (Number.isFinite(limit) && limit > 0) {
    rows.push({ label: `Connect ${limit} website${limit === 1 ? '' : 's'}`, strong: true });
  }

  rows.push({ label: 'Optimize content for conversion' });
  rows.push({ label: 'Unique content relevant to your business' });
  rows.push({ label: 'Internal linking' });
  rows.push({ label: 'Meta titles and descriptions' });
  rows.push({ label: 'Schema markup' });
  rows.push({ label: 'Publish straight inside WordPress' });

  return rows;
}

/**
 * What the BASIC (free, pay-as-you-go) plan offers, for its card in the grid.
 *
 * ── WHY IT IS NOT buildPlanFeatures ─────────────────────────────────────────
 * Basic is not a small subscription, and four of the paid rows are untrue on
 * it, in both directions:
 *
 *   "N credits per month"            nothing is granted monthly; you buy packs
 *   "Credits roll over one month"    purchased credits last TWELVE
 *   "Cancel any time"                there is no subscription to cancel
 *   "Bulk Post Creation" /           the higher-tier features the options card
 *   "Scheduled Publishing"           says the paid plans add
 *
 * The rows the two DO share are listed once, in SHARED_FEATURE_ROWS below, so
 * a change to a shared claim cannot land on the paid cards and miss this one.
 *
 * ── WHY THE PACK PRICES ARE PASSED IN ───────────────────────────────────────
 * "$5 for 60 credits ≈ 3 pieces" is three live figures, and the ticket quoted
 * today's values. Typing them in would put a price on screen that a change in
 * the pricing table silently makes false — the exact failure the trial copy
 * was rewritten to remove. They come from the same /credit-packages payload
 * the pack picker and the checkout use, so the card cannot quote a price the
 * customer is not charged.
 *
 * With no packages yet (still loading, or the request failed) the row is
 * DROPPED rather than shown empty: a "Credit pack options" tick with nothing
 * under it reads as "there are none".
 *
 * @param packages   [{ credits, price_cents, approx_pieces }] as served
 * @param formatPrice  money formatter, injected so this stays free of imports
 *                     that would drag Intl into a pure copy module's tests
 */
export function buildBasicPlanFeatures({ packages = [], formatPrice } = {}) {
  const rows = [];

  const packBullets = (Array.isArray(packages) ? packages : [])
    .filter((p) => Number(p?.credits) > 0 && Number(p?.price_cents) >= 0)
    .map((p) => {
      const credits = Number(p.credits);
      const pieces = Number(p.approx_pieces) || 0;
      const price = formatPrice ? formatPrice(p.price_cents) : `${p.price_cents}c`;
      return {
        text: `${price} for ${credits.toLocaleString()} credits`,
        // Dropped when the estimate is missing, for the same reason the paid
        // card drops it: an absent sub-line is invisible, "≈ 0 pieces" is not.
        ...(pieces ? {
          detail: `≈ ${pieces.toLocaleString()} ${pieces === 1 ? 'piece' : 'pieces'} `
            + 'of local SEO content with AI image',
        } : {}),
      };
    });

  if (packBullets.length) {
    rows.push({ label: 'Credit pack options', strong: true, bullets: packBullets });
  }

  rows.push({
    label: 'Flexible: use credits as you choose for',
    // The same two the paid cards list. This card had two while they had three
    // ("Keyword lists"); the paid list dropped its third rather than this one
    // gaining it, because the keyword-list TOOL is a shared row further down.
    bullets: ['Local SEO content generation', 'AI images'],
  });

  // TWELVE months, against the paid plans' two. This is the row people compare,
  // and it is the one thing pay-as-you-go is unambiguously better at.
  rows.push({ label: 'Credits valid for 12 months', strong: true });
  rows.push({ label: 'Connect 1 website', strong: true });

  // Ends with the keyword-list tool and the WordPress row, which used to be
  // pushed here by hand. They are shared claims, so they moved into the shared
  // list — the order this card renders is unchanged.
  for (const label of SHARED_FEATURE_ROWS) rows.push({ label });

  return rows;
}
